import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import AppleLogin from 'react-apple-login';
import { appleSignInState } from '../../_utils/appleSignIn';
import AuthLayout from './AuthLayout';
import ClientCarousel from './ClientCarousel';
import CodeInput from './components/CodeInput';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import ResendButton from './components/ResendButton';
import { userActions } from '../../_actions';
import siteSetting from '../../config/env';
import './auth.css';

// UK habit: typing the leading 0 after the +44 ("+44 07700…") would make an
// invalid number. A valid +44 number never has 0 next, so drop it as they type.
// Remount-on-blur (phoneKey) re-derives the display text from the value.
const dropTrunkZero = (v) => (v && v.startsWith('+440') ? `+44${v.slice(4)}` : v);

class ClientSignup extends Component {
  constructor(props) {
    super(props);
    
    // Get return URL from query params. If it's missing but a booking is
    // waiting in localStorage (user detoured via the role selector or a
    // plain /signup link and the query param got dropped), recover it from
    // there — otherwise a successful signup redirects to /dashboard/bookings,
    // and PrivateRoute bounces any hiccup straight out to the marketplace,
    // losing the booking flow entirely.
    const urlParams = new URLSearchParams(window.location.search);
    let returnUrl = urlParams.get('returnUrl');
    if (!returnUrl) {
      try {
        const pending = JSON.parse(localStorage.getItem('pendingBooking'));
        if (pending && pending.returnUrl) {
          returnUrl = encodeURIComponent(pending.returnUrl);
        }
      } catch (e) { /* corrupt pendingBooking — ignore */ }
    }

    this.state = {
      first_name: '',
      last_name: '',
      email: '',
      phoneNumber: '',
      phoneKey: 0,
      password: '',
      fNameError: '',
      lNameError: '',
      emailError: '',
      phoneNumberError: '',
      passwordError: '',
      error: '',
      isLoading: false,
      passwordStrength: 0,
      returnUrl: returnUrl ? decodeURIComponent(returnUrl) : null,
      
      // Email verification flow state
      showVerification: false,
      verificationCode: ['', '', '', '', '', ''],
      codeError: '',
      existingUserEmail: '',

      // Reconciliation: an unclaimed salon-created account exists and a
      // claim link has been emailed. Nothing was created.
      claimLinkSent: false
    };
    
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.validateEmail = this.validateEmail.bind(this);
    this.checkPasswordStrength = this.checkPasswordStrength.bind(this);
    this.handleCodeChange = this.handleCodeChange.bind(this);
    this.handleCodeSubmit = this.handleCodeSubmit.bind(this);
    this.handleResendCode = this.handleResendCode.bind(this);
    this.handleBackToSignup = this.handleBackToSignup.bind(this);
  }

  componentDidMount() {
    // The booking modals lock body scroll (position:fixed) while open, and a
    // navigation mid-lock can leave that lock behind — this page then can't
    // scroll at all. An auth page never needs a locked body: clear it.
    const bs = document.body.style;
    if (bs.position === 'fixed') {
      bs.overflow = '';
      bs.position = '';
      bs.width = '';
      bs.top = '';
    }
    // The salon page's auth prompt navigates here via history.push while its
    // scroll lock is still active; the lock's unmount cleanup restores the
    // salon page's scroll offset onto this page, leaving the form opened
    // mid-scroll on mobile. Always start at the top.
    window.scrollTo(0, 0);
  }

  handleChange(e) {
    const { name, value } = e.target;
    this.setState({ 
      [name]: value,
      [`${name}Error`]: '',
      error: ''
    });

    // Check password strength
    if (name === 'password') {
      this.checkPasswordStrength(value);
    }
  }

  validateEmail(email) {
    const pattern = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(email);
  }

  checkPasswordStrength(password) {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    
    this.setState({ passwordStrength: strength });
  }

  handleSubmit(e) {
    e.preventDefault();
    
    // Reset errors
    this.setState({
      fNameError: '',
      lNameError: '',
      emailError: '',
      phoneNumberError: '',
      passwordError: '',
      error: ''
    });

    const { first_name, last_name, email, phoneNumber, password } = this.state;
    let hasError = false;

    // Validation
    if (!first_name || first_name.length < 2) {
      this.setState({ fNameError: 'Please enter a valid first name (min 2 characters)' });
      hasError = true;
    }

    if (!last_name || last_name.length < 2) {
      this.setState({ lNameError: 'Please enter a valid last name (min 2 characters)' });
      hasError = true;
    }

    if (!email) {
      this.setState({ emailError: 'Please enter your email address' });
      hasError = true;
    } else if (!this.validateEmail(email)) {
      this.setState({ emailError: 'Please enter a valid email address' });
      hasError = true;
    }

    if (!password) {
      this.setState({ passwordError: 'Please enter a password' });
      hasError = true;
    } else if (password.length < 8) {
      this.setState({ passwordError: 'Password must be at least 8 characters' });
      hasError = true;
    }

    // Phone is optional, but when given it must be a real number for the
    // selected country (held in E.164, UK default — see the PhoneInput field).
    if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
      this.setState({ phoneNumberError: 'Please enter a valid phone number' });
      hasError = true;
    }

    if (hasError) return;

    // First check if email already exists
    this.setState({ isLoading: true });
    
    this.props.dispatch(userActions.checkEmailExists({ email }))
      .then((response) => {
        // Check the response structure from the API
        // statusCode: 1 = success (email available), statusCode: 0 = error (email exists)
        if (response.data && response.data.statusCode === 1) {
          // Email doesn't exist - proceed with normal signup
          this.proceedWithSignup();
        } else if (response.data && response.data.statusCode === 0) {
          // Email exists - send verification code and show verification UI
          this.props.dispatch(userActions.sendSignupVerificationCode({ email }))
            .then(() => {
              this.setState({
                isLoading: false,
                showVerification: true,
                existingUserEmail: email
              });
            })
            .catch((sendError) => {
              this.setState({
                isLoading: false,
                error: 'Failed to send verification code. Please try again.'
              });
            });
        } else {
          // Unknown response - proceed with signup anyway
          this.proceedWithSignup();
        }
      })
      .catch((error) => {
        // API error - check if it's "email already exists" error
        const errorData = error.response?.data;
        if (errorData && errorData.statusCode === 0 && errorData.error) {
          const errorMessage = errorData.error.responseMessage || '';
          if (errorMessage.toLowerCase().includes('email') &&
              (errorMessage.toLowerCase().includes('already') || errorMessage.toLowerCase().includes('use'))) {
            // Email exists - send verification code and show verification UI
            this.props.dispatch(userActions.sendSignupVerificationCode({ email }))
              .then(() => {
                this.setState({
                  isLoading: false,
                  showVerification: true,
                  existingUserEmail: email
                });
              })
              .catch((sendError) => {
                this.setState({
                  isLoading: false,
                  error: 'Failed to send verification code. Please try again.'
                });
              });
            return;
          }
        }
        // Other error - try to proceed with signup anyway
        this.proceedWithSignup();
      });
  }

  proceedWithSignup() {
    const { first_name, last_name, email, phoneNumber, password } = this.state;
    
    const params = {
      name: `${first_name} ${last_name}`,
      first_name: first_name,
      last_name: last_name,
      email: email,
      phoneNumber: phoneNumber || '',
      password: password,
      userType: 3, // Client
      deviceToken: 'web',
      deviceTypeId: '2'
    };

    this.props.dispatch(userActions.signup(params))
      .then((response) => {
        this.setState({ isLoading: false });
        
        if (response.data.statusCode === 0) {
          // Reconciliation: an unclaimed salon-created account exists for
          // this email and a claim link is in their inbox. This must run
          // BEFORE the "email exists" sniffing below — the code-verification
          // recovery can't log in override-only accounts (their User.email is
          // still a placeholder), but the emailed link can.
          if (response.data.outcome === 'claim_sent') {
            this.setState({ error: '', claimLinkSent: true });
            return;
          }
          // Check if the error is about email already existing
          const errorMessage = response.data.error?.responseMessage || '';
          if (errorMessage.toLowerCase().includes('email') &&
              (errorMessage.toLowerCase().includes('exist') || errorMessage.toLowerCase().includes('already'))) {
            // Email exists - send verification code and show verification UI
            this.props.dispatch(userActions.sendSignupVerificationCode({ email }))
              .then(() => {
                this.setState({
                  showVerification: true,
                  existingUserEmail: email,
                  error: ''
                });
              })
              .catch(() => {
                this.setState({
                  error: 'This email is already registered. Please sign in instead.'
                });
              });
          } else {
            this.setState({
              error: errorMessage || 'Signup failed. Please try again.'
            });
          }
        } else {
          // Success per the API — but only trust it if the session token
          // actually landed in storage. Redirecting without it strands the
          // user logged out mid-booking (PrivateRoute then bounces them to
          // the marketplace and the flow is lost).
          if (!localStorage.getItem('accessToken')) {
            this.setState({
              error: 'Your account was created but we could not sign you in automatically. Please sign in below to continue.'
            });
            return;
          }

          // Stamp acceptance of the current Client Terms + Privacy
          // Policy versions. Signup goes through the legacy v1 API which
          // can't record this, so the freshly-minted session does it here.
          // Fire-and-forget: a logging failure must never block the signup.
          try {
            const axios = require('axios');
            const siteSetting = require('../../config/env').default;
            const headers = { accessToken: localStorage.getItem('accessToken') };
            ['client_terms', 'privacy_policy'].forEach((t) => {
              axios.post(`${siteSetting.api.v2}policy/${t}/accept`, { context: 'client_signup' }, { headers }).catch(() => {});
            });
          } catch (e) { /* never block signup */ }

          // Success - check if this is a booking flow
          const { returnUrl } = this.state;
          const isBooking = localStorage.getItem('isBooking') === '1';
          const hasPendingBooking = localStorage.getItem('pendingBooking') !== null;
          
          // Check if returnUrl contains booking-slot (booking flow from URL)
          // OR if there's a pending booking from the salon profile page
          const isBookingFlow = returnUrl && (
            returnUrl.includes('booking-slot') ||
            returnUrl.includes('/salon/') ||
            hasPendingBooking
          );
          
          if (isBookingFlow || (isBooking && returnUrl)) {
            // Redirect back to booking page
            window.location.href = returnUrl;
          } else if (returnUrl) {
            // Has a return URL - redirect there
            window.location.href = returnUrl;
          } else {
            // Regular signup - redirect to bookings
            this.props.history.push('/dashboard/bookings');
          }
        }
      })
      .catch((error) => {
        this.setState({
          isLoading: false,
          error: 'An error occurred. Please try again.'
        });
      });
  }

  handleCodeChange(newCode) {
    this.setState({
      verificationCode: newCode,
      codeError: ''
    });

    // Auto-submit when all 6 digits are entered
    if (newCode.every(digit => digit !== '')) {
      setTimeout(() => this.handleCodeSubmit(), 100);
    }
  }

  handleCodeSubmit() {
    const { existingUserEmail, verificationCode, password } = this.state;
    const code = verificationCode.join('');

    if (code.length !== 6) {
      this.setState({ codeError: 'Please enter the complete 6-digit code' });
      return;
    }

    this.setState({ isLoading: true, codeError: '' });

    this.props.dispatch(userActions.verifySignupCode({ email: existingUserEmail, code }))
      .then((response) => {
        // Check for verified status - API returns responseData.verified
        const isVerified = response.data &&
          ((response.data.responseData && response.data.responseData.verified) ||
           (response.data.data && response.data.data.verified));
        
        if (isVerified) {
          // Code verified - now reset password and login
          // One-time token from the verify step; the server requires it.
          const rd = (response.data && (response.data.responseData || response.data.data)) || {};
          this.props.dispatch(userActions.resetPasswordAndLogin({
            email: existingUserEmail,
            resetToken: rd.resetToken,
            newPassword: password
          }))
            .then((loginResponse) => {
              this.setState({ isLoading: false });

              // The action persists the token before resolving — trust the
              // stored token, not the response shape. A shape the action
              // didn't recognise used to pass this check and redirect a
              // logged-OUT user out of the booking flow.
              const loginData = !!localStorage.getItem('accessToken');

              if (loginData) {
                // Success - redirect with full page reload to ensure user data is fetched
                const { returnUrl } = this.state;
                const isBooking = localStorage.getItem('isBooking') === '1';
                const hasPendingBooking = localStorage.getItem('pendingBooking') !== null;
                const isBookingFlow = returnUrl && (
                  returnUrl.includes('booking-slot') ||
                  returnUrl.includes('/salon/') ||
                  hasPendingBooking
                );
                
                if (isBookingFlow || (isBooking && returnUrl)) {
                  window.location.href = returnUrl;
                } else if (returnUrl) {
                  // Has a return URL - redirect there
                  window.location.href = returnUrl;
                } else {
                  // Use window.location.href instead of history.push to force full page reload
                  // This ensures the app fetches the user profile after login
                  window.location.href = '/dashboard/bookings';
                }
              } else {
                this.setState({
                  error: 'Failed to complete authentication. Please try again.'
                });
              }
            })
            .catch((error) => {
              this.setState({
                isLoading: false,
                error: error.response?.data?.error?.responseMessage || 'Failed to complete authentication.'
              });
            });
        } else {
          this.setState({
            isLoading: false,
            codeError: 'Invalid verification code. Please try again.'
          });
        }
      })
      .catch((error) => {
        this.setState({
          isLoading: false,
          codeError: error.response?.data?.error?.responseMessage || 'Invalid verification code'
        });
      });
  }

  handleResendCode() {
    const { existingUserEmail } = this.state;
    
    this.setState({ isLoading: true, codeError: '' });

    this.props.dispatch(userActions.sendSignupVerificationCode({ email: existingUserEmail }))
      .then(() => {
        this.setState({ isLoading: false });
      })
      .catch((error) => {
        this.setState({
          isLoading: false,
          codeError: error.response?.data?.error?.responseMessage || 'Failed to resend code'
        });
      });
  }

  handleBackToSignup() {
    this.setState({
      showVerification: false,
      verificationCode: ['', '', '', '', '', ''],
      codeError: '',
      error: ''
    });
  }

  getPasswordStrengthClass() {
    const { passwordStrength } = this.state;
    if (passwordStrength <= 1) return 'weak';
    if (passwordStrength <= 2) return 'medium';
    return 'strong';
  }

  getPasswordStrengthText() {
    const { passwordStrength } = this.state;
    if (passwordStrength === 0) return '';
    if (passwordStrength <= 1) return 'Weak';
    if (passwordStrength <= 2) return 'Medium';
    return 'Strong';
  }

  renderVerificationPanel() {
    const { existingUserEmail, verificationCode, codeError, isLoading, error } = this.state;

    return (
      <div className="auth-form-container">
        <button
          type="button"
          className="auth-nav-back"
          onClick={this.handleBackToSignup}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <h1 className="auth-title">Authentication Needed</h1>
        <p className="auth-subtitle">Verify your existing account</p>

        <div className="auth-verification-header">
          <p className="auth-verification-subtitle">
            An account with this email already exists. We've sent a verification code to <strong>{existingUserEmail}</strong>
          </p>
        </div>

        {error && (
          <div className="auth-error auth-mb-md">
            <span>{error}</span>
          </div>
        )}

        <div className="auth-form-group">
          <label className="auth-label">Enter verification code</label>
          <CodeInput
            code={verificationCode}
            onChange={this.handleCodeChange}
            error={codeError}
            disabled={isLoading}
          />
        </div>

        <div className="auth-resend-section">
          <p className="auth-text-muted" style={{ marginBottom: '8px' }}>Didn't receive it?</p>
          <ResendButton
            onResend={this.handleResendCode}
            initialCountdown={60}
            disabled={isLoading}
          />
        </div>

        <button
          type="button"
          className="auth-button auth-button-secondary auth-mt-md"
          onClick={this.handleBackToSignup}
          disabled={isLoading}
        >
          Use different email
        </button>
      </div>
    );
  }

  renderSignupForm() {
    const {
      first_name,
      last_name,
      email,
      phoneNumber,
      password,
      fNameError,
      lNameError,
      emailError,
      phoneNumberError,
      passwordError,
      error,
      isLoading,
      passwordStrength,
      returnUrl
    } = this.state;

    // Build login URL with return URL if present
    const loginUrl = returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login';

    return (
      <div className="auth-form-container">
        <Link to={returnUrl || "/signup"} className="auth-nav-back">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Link>

        <h1 className="auth-title">Create Your Account</h1>
        <p className="auth-subtitle">Quick and easy setup</p>

        {this.state.claimLinkSent && (
          <div className="auth-mb-md" style={{
            padding: '12px 16px', borderRadius: 8, fontSize: 14,
            background: '#e7f5ee', color: '#0f5132'
          }}>
            <strong>You already have a Beu account.</strong> Your salon set it
            up for you, and we've emailed you a link to activate it. Open the link,
            set your password, and all your bookings will be there.
          </div>
        )}

        {error && (
          <div className="auth-error auth-mb-md">
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={this.handleSubmit}>
          <div className="auth-form-group">
            <label className="auth-label" htmlFor="first_name">First Name</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              className={`auth-input ${fNameError ? 'error' : ''}`}
              placeholder="Enter your first name"
              value={first_name}
              onChange={this.handleChange}
              autoComplete="given-name"
              autoFocus={window.innerWidth > 768}
            />
            {fNameError && (
              <div className="auth-error">
                <span>{fNameError}</span>
              </div>
            )}
          </div>

          <div className="auth-form-group">
            <label className="auth-label" htmlFor="last_name">Last Name</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              className={`auth-input ${lNameError ? 'error' : ''}`}
              placeholder="Enter your last name"
              value={last_name}
              onChange={this.handleChange}
              autoComplete="family-name"
            />
            {lNameError && (
              <div className="auth-error">
                <span>{lNameError}</span>
              </div>
            )}
          </div>

          <div className="auth-form-group">
            <label className="auth-label" htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              className={`auth-input ${emailError ? 'error' : ''}`}
              placeholder="Enter your email"
              value={email}
              onChange={this.handleChange}
              autoComplete="email"
            />
            {emailError && (
              <div className="auth-error">
                <span>{emailError}</span>
              </div>
            )}
          </div>

          <div className="auth-form-group">
            <label className="auth-label" htmlFor="phoneNumber">
              Phone Number <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}></span>
            </label>
            <PhoneInput
              key={this.state.phoneKey}
              id="phoneNumber"
              international
              defaultCountry="GB"
              countryCallingCodeEditable={false}
              autoComplete="tel"
              placeholder="+44 7700 900000"
              value={phoneNumber}
              onChange={(value) => this.setState({ phoneNumber: dropTrunkZero(value) || '', phoneNumberError: '' })}
              onBlur={() => this.setState((st) => ({ phoneKey: st.phoneKey + 1 }))}
              className={`auth-phone ${phoneNumberError ? 'auth-phone--error' : ''}`}
            />
            {phoneNumberError && (
              <div className="auth-error">
                <span>{phoneNumberError}</span>
              </div>
            )}
          </div>

          <div className="auth-form-group">
            <label className="auth-label" htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              className={`auth-input ${passwordError ? 'error' : ''}`}
              placeholder="Create a password"
              value={password}
              onChange={this.handleChange}
              autoComplete="new-password"
            />
            {password && passwordStrength > 0 && (
              <div className="auth-password-strength">
                <div className={`auth-password-strength-bar ${this.getPasswordStrengthClass()}`}></div>
              </div>
            )}
            {password && passwordStrength > 0 && (
              <div className="auth-success" style={{ marginTop: '4px' }}>
                <span>Password strength: {this.getPasswordStrengthText()}</span>
              </div>
            )}
            {passwordError && (
              <div className="auth-error">
                <span>{passwordError}</span>
              </div>
            )}
          </div>

          <button
            type="submit"
            className={`auth-button auth-button-primary ${isLoading ? 'auth-button-loading' : ''}`}
            disabled={isLoading}
          >
            Create Account
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <AppleLogin
          clientId={siteSetting.apple_clientId}
          redirectURI={siteSetting.apple_redirectURI}
          state={appleSignInState()}
          scope="name email"
          responseType="code id_token"
          responseMode="form_post"
          render={(props) => (
            <button
              className="auth-button auth-button-social"
              onClick={props.onClick}
              disabled={props.disabled}
            >
              <svg width="20" height="20" viewBox="0 0 384 512" fill="currentColor">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
              Continue with Apple
            </button>
          )}
        />

        <p className="auth-helper-text" style={{ textAlign: 'center', marginTop: '24px' }}>
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="auth-link" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="auth-link" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
        </p>
        <div className="auth-footer-link">
          <span className="auth-text-muted">Already have an account? </span>
          <Link to={loginUrl} className="auth-link">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  render() {
    const { showVerification } = this.state;

    // When showing verification, render the verification panel on the left
    // and keep the carousel on the right
    if (showVerification) {
      return (
        <AuthLayout rightPanel={<ClientCarousel />}>
          {this.renderVerificationPanel()}
        </AuthLayout>
      );
    }

    // Normal signup form
    return (
      <AuthLayout rightPanel={<ClientCarousel />}>
        {this.renderSignupForm()}
      </AuthLayout>
    );
  }
}

function mapStateToProps(state) {
  const { registering } = state.registration || {};
  return { registering };
}

export default connect(mapStateToProps)(ClientSignup);